import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/my-quotes');
      return;
    }

    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/package/status/${sessionId}`);
      
      if (response.data.paymentStatus === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else if (pollCount < 10) {
        // Continue polling
        setPollCount(pollCount + 1);
        setTimeout(checkPaymentStatus, 2000); // Poll every 2 seconds
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (pollCount < 10) {
        setPollCount(pollCount + 1);
        setTimeout(checkPaymentStatus, 2000);
      } else {
        setStatus('failed');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card>
          <CardContent className="p-12 text-center">
            {status === 'checking' && (
              <>
                <Loader2 className="h-16 w-16 text-orange-600 mx-auto mb-4 animate-spin" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
                <p className="text-gray-600">Please wait while we confirm your payment</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
                <p className="text-gray-600 mb-6">Your payment has been processed successfully</p>
                
                {paymentDetails && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Amount Paid:</span>
                      <span className="font-semibold text-gray-900">
                        ${paymentDetails.amount.toFixed(2)} {paymentDetails.currency.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      The service provider will contact you shortly to schedule your service.
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate('/my-quotes')}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    View My Quotes
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="flex-1"
                  >
                    Back to Home
                  </Button>
                </div>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="h-10 w-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
                <p className="text-gray-600 mb-6">
                  We couldn't process your payment. Please try again or contact support.
                </p>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => navigate('/my-quotes')}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Back to Quotes
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="flex-1"
                  >
                    Contact Support
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
